import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Dialog } from '../../../components/Dialog';
import { useAlerts } from '../../../hooks/use-alerts';
import { Spinner } from '../../../components/Spinner';

export function Adjustments(props) {
  const { Component, field, title, handleClose, handleSave, canAdjust } = props;
  const [isLoading, setLoading] = useState(true);
  const [adjustments, setAdjustments] = useState({});
  const [isSaving, setSaving] = useState(false);
  const { showError } = useAlerts();
  const revertRef = useRef(() => {});

  const onLoad = useCallback(
    (_, groups, fieldAdjustment) => {
      if (!groups) return showError();
      setLoading(false);
      setAdjustments({
        base: { groups, fieldAdjustment },
        changed: { groups, fieldAdjustment },
      });
    },
    [showError],
  );

  const saveChanges = useCallback(() => {
    setSaving(true);

    handleSave(
      field,
      adjustments.changed?.fieldAdjustment,
      adjustments.changed?.groups?.map((g) => ({
        maxValue: g.value,
        adjustmentFactor: g.adjustment,
      })),
    )
      .catch(showError)
      .finally(() => setSaving(false));
  }, [adjustments, field, handleSave, showError]);

  const revertChanges = useCallback(() => {
    revertRef.current();
  }, []);

  const canRevert = useCallback(() => {
    const changes = [
      adjustments.changed.fieldAdjustment,
      ...adjustments.changed.groups.map((g) => g.adjustment),
    ];
    return !isSaving && changes.some(Boolean);
  }, [adjustments.changed, isSaving]);

  const actions = useMemo(
    () =>
      isLoading || !canAdjust
        ? false
        : [
            <Button
              key="cancel"
              onClick={handleClose}
              color="inherit"
              variant="contained"
            >
              Close
            </Button>,
            <Button
              key="revert"
              onClick={revertChanges}
              color="inherit"
              variant="contained"
              disabled={!canRevert()}
            >
              Revert
            </Button>,
            <LoadingButton
              key="save"
              onClick={saveChanges}
              color="primary"
              variant="contained"
              loading={isSaving}
            >
              Save
            </LoadingButton>,
          ],
    [
      canRevert,
      handleClose,
      isLoading,
      isSaving,
      revertChanges,
      saveChanges,
      canAdjust,
    ],
  );

  const onGroupsAdjustment = useCallback(
    (_, groups) =>
      setAdjustments((current) => {
        const cloned = structuredClone(current);
        cloned.changed.groups = groups;
        return cloned;
      }),
    [],
  );

  const onFieldAdjustment = useCallback(
    (_, adjustment) =>
      setAdjustments((current) => {
        const cloned = structuredClone(current);
        cloned.changed.fieldAdjustment = adjustment;
        return cloned;
      }),
    [],
  );

  return (
    <Dialog title={title} onCancel={handleClose} actions={actions}>
      <Box sx={{ minHeight: 500 }}>
        {isLoading ? <Spinner sx={{ minHeight: 500 }} /> : null}

        <Component
          field={field}
          isVisible
          onClose={handleClose}
          onFieldAdjustment={onFieldAdjustment}
          onGroupsAdjustment={onGroupsAdjustment}
          onLoad={onLoad}
          revertRef={revertRef}
        />
      </Box>
    </Dialog>
  );
}
